import { useState } from 'react';
import { Button, Input, Label } from '@staff-app/ui';
import type { DeviceRmaRequest } from '@staff-app/shared-types';
import { MapPin, Undo2 } from 'lucide-react';
import { useSetDeviceRmaShippingAddress, type RmaShipToInput } from '../hooks/useRma';

/** The five a correction must carry. Mirrors REQUIRED_SHIP_TO on the server and the CHECK in 047. */
const REQUIRED = ['name', 'line1', 'city', 'zip', 'country'] as const;

type Form = RmaShipToInput;

/** The shape `asBlock` reads — nullable, because the stored columns are. */
type BlockParts = { [K in keyof Form]?: string | null };

/** One block, the way it will be printed on a label — not six rows. Mirrors the detail drawer. */
function asBlock(a: BlockParts): string {
  return [a.name, a.line1, a.line2, [a.city, a.state, a.zip].filter(Boolean).join(', '), a.country]
    .filter((l) => l && String(l).trim())
    .join('\n');
}

/**
 * RmaShipToEditor — where the replacement actually goes, and the one customer-looking field on this
 * surface that staff may change.
 *
 * WHY IT IS A CORRECTION AND NOT AN EDIT. The seven `shipTo*` fields belong to the HubSpot
 * submission and the five-minute poll rewrites every one of them, which is why the detail drawer is
 * read-only and says so. Writing here would have been silently reverted — most likely AFTER
 * somebody released a replacement against it. So the correction lands in staff-app's own columns
 * (migration 047) and the customer's own address survives untouched beside it, which is the record
 * that matters when a parcel goes missing.
 *
 * ALL-OR-NOTHING, enforced here as well as on the server and in the CHECK. Save is disabled until
 * the five required fields are filled. A per-field edit was the obvious alternative and produces
 * the failure this is guarding: a corrected street on the submitted city, which looks deliverable
 * and is not.
 *
 * ⚠️ IT CANNOT MOVE A REPLACEMENT THAT HAS ALREADY SHIPPED. A Shopify order carries the address it
 * was created with and nothing here reaches back into it. The correction is still worth recording —
 * the ledger should be right, and the next person needs the real address — so the edit is allowed
 * and the warning is loud rather than the edit being refused.
 */
export function RmaShipToEditor({ row }: { row: DeviceRmaRequest }) {
  const [save, { loading }] = useSetDeviceRmaShippingAddress();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `verifiedShipToLine1` is a reliable marker for "is there a correction?" precisely because the
  // stored shape is all-or-nothing.
  const corrected = Boolean(row.verifiedShipToLine1);
  const effective: BlockParts = corrected
    ? {
        name: row.verifiedShipToName ?? '',
        line1: row.verifiedShipToLine1 ?? '',
        line2: row.verifiedShipToLine2 ?? '',
        city: row.verifiedShipToCity ?? '',
        state: row.verifiedShipToState ?? '',
        zip: row.verifiedShipToZip ?? '',
        country: row.verifiedShipToCountry ?? '',
      }
    : {
        name: row.shipToName ?? '',
        line1: row.shipToLine1 ?? '',
        line2: row.shipToLine2 ?? '',
        city: row.shipToCity ?? '',
        state: row.shipToState ?? '',
        zip: row.shipToZip ?? '',
        country: row.shipToCountry ?? '',
      };

  const [form, setForm] = useState<Form>({
    name: effective.name ?? '',
    line1: effective.line1 ?? '',
    line2: effective.line2 ?? '',
    city: effective.city ?? '',
    state: effective.state ?? '',
    zip: effective.zip ?? '',
    country: effective.country ?? '',
  });

  const missing = REQUIRED.filter((k) => !String(form[k] ?? '').trim());

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (address: Form | null) => {
    setError(null);
    try {
      await save({ variables: { submissionId: row.submissionId, address } });
      setEditing(false);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const field = (k: keyof Form, label: string, required = true) => (
    <div>
      <Label htmlFor={`rma-ship-${k}`} className="text-xs">
        {required ? label : `${label} (optional)`}
      </Label>
      <Input
        id={`rma-ship-${k}`}
        value={form[k] ?? ''}
        onChange={set(k)}
        className="mt-1"
        data-testid={`rma-ship-${k}`}
      />
    </div>
  );

  return (
    <div className="rounded-md border p-3" data-testid="rma-ship-to">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-1 text-sm font-semibold">
            <MapPin className="h-4 w-4" />
            {'Ship to'}
            {corrected ? (
              <span
                className="rounded bg-muted px-1.5 py-0.5 text-xs font-normal"
                data-testid="rma-ship-corrected-badge"
              >
                {'corrected'}
              </span>
            ) : null}
          </h3>
          {!editing ? (
            <p className="mt-1 whitespace-pre-wrap text-sm" data-testid="rma-ship-block">
              {asBlock(effective) || '—'}
            </p>
          ) : null}
          {!editing && corrected ? (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="rma-ship-stamp">
              {`Changed by ${row.verifiedShipToBy ?? 'unknown'}${
                row.verifiedShipToAt ? ` on ${new Date(row.verifiedShipToAt).toLocaleString()}` : ''
              }. Customer submitted: ${asBlock({
                name: row.shipToName,
                line1: row.shipToLine1,
                line2: row.shipToLine2,
                city: row.shipToCity,
                state: row.shipToState,
                zip: row.shipToZip,
                country: row.shipToCountry,
              }).replace(/\n/g, ', ') || '—'}`}
            </p>
          ) : null}
        </div>

        {!editing ? (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="rma-ship-edit">
            {'Edit'}
          </Button>
        ) : null}
      </div>

      {/* Loud, and placed where the decision is made rather than in a toast that has gone by the
          time it matters. Once an order exists this edit fixes the LEDGER and nothing else. */}
      {row.replacementOrderId ? (
        <p className="mt-2 text-xs text-destructive" data-testid="rma-ship-shipped-warning">
          {`Replacement ${row.replacementOrderName || 'order'} already exists — changing this does NOT move it. Update the address in Shopify, or cancel and re-create the order there.`}
        </p>
      ) : null}

      {editing ? (
        <div className="mt-3 flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {field('name', 'Recipient')}
            {field('line1', 'Address line 1')}
            {field('line2', 'Address line 2', false)}
            {field('city', 'City')}
            {field('state', 'State / province', false)}
            {field('zip', 'ZIP / postcode')}
            {field('country', 'Country')}
          </div>

          {missing.length ? (
            <p className="text-xs text-muted-foreground" data-testid="rma-ship-missing">
              {`Still needed: ${missing.join(', ')}. A partial address is refused — half of one address and half of another is a parcel that looks deliverable and is not.`}
            </p>
          ) : null}

          {error ? (
            <p className="text-sm text-destructive" data-testid="rma-ship-error">
              {error}
            </p>
          ) : null}

          <div className="flex items-center justify-end gap-2">
            {/* Only offered when there IS something to revert to — on an uncorrected row it would
                be a button that does nothing. */}
            {corrected ? (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1"
                disabled={loading}
                onClick={() => void submit(null)}
                data-testid="rma-ship-revert"
              >
                <Undo2 className="h-4 w-4" />
                {"Use customer's address"}
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)} data-testid="rma-ship-cancel">
              {'Cancel'}
            </Button>
            <Button
              size="sm"
              disabled={loading || missing.length > 0}
              onClick={() => void submit(form)}
              data-testid="rma-ship-save"
            >
              {loading ? 'Saving…' : 'Save address'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
